import { addComponentToCache, CacheKey, getCachedComponent } from './iterator-cache';
import { Activator } from 'app/infrastructure/ui/renderer/activator';
import { 
  connectIteratorComponent, 
  createStateToPropsSelector 
} from 'app/infrastructure/ui/renderer/field-connector';
import * as React from 'react';
import { 
  ComponentDescriptor, ConnectedComponentProps, 
  ChildComponentContext, ComponentContext } from '../types-uischema';
import { Field } from './field';
import { getComponentUid } from 'app/infrastructure/ui/renderer/resolvers';
import { ChildItem, RowProps } from 'app/infrastructure/ui/renderer/iterator-types';

/**
 * Create the correct child CompositeKeys object
 *
 * @param props the outermost host props
 * @param keys calculated CompositeKeys for the child row
 */
export const createRowDataContext = ({
  applicationId,
  path,
  primaryKey,
  virtual,
  defaultValues,
  mode
}: ChildComponentContext): ChildComponentContext => {
  if (!Number.isFinite(applicationId)) {
    throw Error('Invalid applicationId');
  }
  if (!Array.isArray(path) && !virtual) {
    throw Error('Invalid contextual path');
  }
  return ({
    applicationId,
    path,
    primaryKey,
    virtual,
    defaultValues,
    mode
  });
};

/**
 * In the legacy data structure, it's a Parent of the actual render container that has the `iterate` property defined
 *
 * So.. we traverse a bit.
 *
 * Convention #1 - let first descriptor children be the UI definition for our "master" UI
 *
 * @param containersProps 
 */
export const getMasterViewDescriptor = (containerDescriptor: ComponentDescriptor) => {
    const { children } = containerDescriptor;
    return children && children.length && children[0];
  };

/**
 * In the legacy data structure, it's a Parent of the actual render container that has the `iterate` property defined
 *
 * So.. we traverse a bit.
 *
 * Convention #2 - let second descriptor children be the UI definition for our "details" UI
 *
 * @param containersProps 
 */
export const getDetailsViewDescriptor = (containerDescriptor: ComponentDescriptor) => {
  const { children } = containerDescriptor;
  return children && children.length > 1 ? children[1] : undefined;
};

/**
 * Creats a new connected component, or returns one from the cache if it already exists.
 * The caching is to prevent the reconciler from mounting and unmounting components, since
 * react-redux connect() uses createElement internally. 
 * 
 * Note that this need for caching may indicate a design flaw..
 * 
 */
export const getConnectedRowComponent = (rowProps: RowProps) => {
  const rowId: CacheKey = {
    context: { 
      path: rowProps.resolveContext.path, 
      applicationId: rowProps.resolveContext.applicationId, 
      mode: rowProps.resolveContext.mode,
      primaryKey: (rowProps.resolveContext as ChildComponentContext).primaryKey
    },
    id: rowProps.componentDescriptor.id,
    name: rowProps.componentDescriptor.componentName,
    statePath: rowProps.statePath
  };
  const { componentClass, enhancers } = rowProps.componentDescriptor;
  let rowComponent = getCachedComponent(rowId);
  if (!rowComponent && componentClass) {
    rowComponent = connectIteratorComponent(createStateToPropsSelector(rowProps as any))(componentClass);
    
    if (enhancers) {
      rowComponent = enhancers
        .reduce((aggrComponent, wrapper) => wrapper(aggrComponent, rowProps as any), rowComponent);
    }   

    addComponentToCache(rowId, rowComponent);
  }
  return rowComponent || null;
};

/**
 * Iterating downwards in the hierarchy requires that we base props on it's parent props before 
 *  connecting the component. This ensures that `connectComponent` has the props it needs to 
 *  establish the connection between the Component and the redux Store.
 * 
 * @param parentProps Parent components props. 
 * `actions, activate, hostProps, date utilitites, permissions` will be the included in the return object
 * @param childDescriptor Component descriptor to use
 * @param resolveContext ResolveContext with current data path
 * @param optionalProps Any extra to include
 */
const createChildPropsFromParentProps = (
  {
    actions,
    activate,
    activeChildItem,
    hostProps,
    formatDate, formatNumber, formatTime, createDateAsUtc, cloneDate, parseAsDateUTC,
    enabledChildEntityOperations,
    statePath
  }: ConnectedComponentProps,
  childDescriptor: ComponentDescriptor,
  resolveContext: ComponentContext,
  optionalProps: { [key: string]: any } = {}

) => ({
  actions,
  activate,
  activeChildItem,
  formatDate, formatNumber, formatTime, createDateAsUtc, cloneDate, parseAsDateUTC,
  hostProps,
  enabledChildEntityOperations,
  statePath,
  componentDescriptor: childDescriptor,
  resolveContext,
  ...optionalProps
} as any);

/**
 * The Fields in the Form/View that expands when an item is selected
 * 
 * @param containerProps The outermost props
 * @param rowProps The Tile`s props
 * @param keys CompositeKeys to the source data row
 */
export const renderDetailsView = (rowProps: RowProps) => {
  const detailsViewDescriptor = rowProps.detailsViewDescriptor;
  const DetailsComponent =
    (detailsViewDescriptor && detailsViewDescriptor.componentClass) || null;

  if (!DetailsComponent) {
    return null;
  }

  const uid = getComponentUid(rowProps.resolveContext);

  const fieldsInsideDetailsComponent = detailsViewDescriptor.children && detailsViewDescriptor.children.map(
    (detailFieldDescriptor: ComponentDescriptor) => {
      const fieldProps = createChildPropsFromParentProps(
        rowProps,
        detailFieldDescriptor,
        rowProps.resolveContext
      );

      return (
        <Field
          {...fieldProps}
          key={`${detailFieldDescriptor.id}`}
        />
      );
    }
  );

  return (
    <DetailsComponent
      key={`${uid}-details`}
      {...rowProps}
    >
      {fieldsInsideDetailsComponent}
    </DetailsComponent>
  );
};

/**
 * One Field is rendered per column of data in the source table
 */
export const renderFieldsPerColumn = (rowProps: RowProps) =>
  rowProps.componentDescriptor && rowProps.componentDescriptor.children &&
  rowProps.componentDescriptor.children.map(
    (fieldDescriptor: ComponentDescriptor, index: number) => {
      const fieldProps: ConnectedComponentProps = createChildPropsFromParentProps(
        rowProps,
        fieldDescriptor,
        rowProps.resolveContext
      );
      
      return <Field {...fieldProps} key={index} />;
    }
  );

/**
 * Renders the concrete React component representing the row hosting field components per column. 
 * 
 * @param tileOrRowDescriptor 
 * @param keys 
 * @param rowProps 
 */
export const renderRowComponentWithChildren = (rowProps: RowProps) => {
  const TileOrRow = getConnectedRowComponent(rowProps);
  const details = renderDetailsView(rowProps);
  const fieldsPerColumn = renderFieldsPerColumn(rowProps);

  const uid = getComponentUid(rowProps.resolveContext);

  return (
    <TileOrRow key={uid} details={details} {...rowProps}>
      {fieldsPerColumn}
    </TileOrRow>
  );
};

/**
 * - Invoked when a `ComponentDescriptor` in the hierarchy as a defined `iterate` property. 
 *  
 *    - This property contains the name of a *child table* to produce one UI element per *row of data*. 
 *      Each row UI element has one or more `Fields` connected to a *Column in that row*. 
 *
 *    - The children of a Iterator has configuration to describe what columns in that row of data should be 
 *      presented in the UI. This can be thought of as a "row template".
 *
 *    - The "row template" must be repeated for each row in the *child table* that the iterating component defines.
 *    - Children in configuration is used to create row templates for iterative rendering
 *    - By convention, **first child** is the master data view, **second child** is the detail data view
 *
 *    > Why we do this:
 *    >- Configuration only describes what *columns* to display for each row of data, so we'll have to create 
 *        a "template function" to call for each row of data in the target "child table".
 *    >- The convention of first and second child mentioned is unfortunatly a *coincidential design choice* and should
 *        be target in future improvements to config migration and format.
 *       
 */
export const createIterator = (ContainerComponent: React.ComponentClass<any> | null) =>
  /**
   * @param containerProps {ConnectedComponentProps} The redux connect-ed props of the parent container. 
   * For a Iterator could be e.g a TileView, and createIterator will invoke render functions for each Tile and 
   * it's children, per row of data in `containerProps.childItems`
   */
  (
    containerProps: ConnectedComponentProps
  ) => {
    const { resolveContext, componentDescriptor } = containerProps;

    const applicationId = resolveContext && resolveContext.applicationId;
    const tileOrRowDescriptor = componentDescriptor && getMasterViewDescriptor(componentDescriptor);
    const detailsViewDescriptor = componentDescriptor && getDetailsViewDescriptor(componentDescriptor);
    const defaultValues = componentDescriptor && componentDescriptor.iterate && componentDescriptor.iterate.defaultValues;
    const mode = resolveContext && resolveContext.mode ? resolveContext.mode : 0;
    
    /**
     * Renders a UI element for *one* row of data. 
     * This is typically a `Tile` or `Row` UI element.
     * Child elements representing each column gets rendered as children.
     *
     * @param path 
     */
    const renderRow = (childItem: ChildItem, index: number) => {
      
      // The store path is only present if this is a physical data row.
      // For virtual rows, there will not be a context, and it will not be possible to bind data within the tile.
      const path = childItem.storePath;
      const primaryKey = childItem.primaryKey;

      const rowDataContext = createRowDataContext({ applicationId, path, primaryKey, virtual: childItem.virtual, defaultValues, mode });
      const activeChild = childItem.storePath.length === containerProps.activeChildItem?.length && containerProps.activeChildItem || 
        index === containerProps.childItems.length - 1;
      const rowProps = createChildPropsFromParentProps(
        containerProps,
        tileOrRowDescriptor,
        rowDataContext,
        { detailsViewDescriptor, activeChild }
      ) as RowProps;

      const activatorKey = `activator${path && '-'}${JSON.stringify(path)}-${JSON.stringify(
        tileOrRowDescriptor.id
      )}`;

      return (
        <Activator {...rowProps} key={activatorKey}>
          {renderRowComponentWithChildren(rowProps)}
        </Activator>
      );
    };

    // There are no childrows present, don't bother render the TileView/Grid host component
    if (!containerProps.childItems) {
      return null;
    }

    const children = containerProps.childItems.map(renderRow);

    return (
      <ContainerComponent key={containerProps.componentId} {...containerProps}>
        {children}
      </ContainerComponent>
    );
  };
